import { useState } from 'react';

const SERVICES_OPTIONS = [
  'Office Cleaning', 'Janitorial Services', 'Floor Care & Maintenance',
  'Carpet Cleaning', 'Restroom Sanitation', 'Post-Construction Cleaning',
  'Facility Maintenance', 'Parking Lot Cleaning', 'Other / Multiple Services',
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d6va6p67ipaugi04lhvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="bg-navy pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'linear-gradient(rgba(0,230,122,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,230,122,0.15) 1px, transparent 1px)', backgroundSize: '60px 60px' }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-accent text-xs font-body font-medium uppercase tracking-wider">Get In Touch</span>
          </div>
          <h1 className="font-heading font-extrabold text-white text-5xl xl:text-6xl mb-4 leading-tight">
            Contact <span className="text-accent">OJS Janitorial</span>
          </h1>
          <p className="text-white/60 font-body text-lg max-w-xl mx-auto">
            Ready for a cleaner facility? Get a free, customized cleaning quote. We respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left — Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl p-7 border border-gray-100">
                <h2 className="font-heading font-bold text-navy text-xl mb-5">Contact Information</h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-xl shrink-0">
                      <i className="ri-map-pin-line text-accent text-lg"></i>
                    </div>
                    <div>
                      <div className="font-heading font-bold text-navy text-sm mb-1">Office Address</div>
                      <p className="text-gray-500 font-body text-sm leading-relaxed">
                        7809 Airline Drive, Suite #307 F<br />Metairie, LA 70003
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-xl shrink-0">
                      <i className="ri-phone-line text-accent text-lg"></i>
                    </div>
                    <div>
                      <div className="font-heading font-bold text-navy text-sm mb-1">Phone</div>
                      <a href="tel:+15048183347" className="text-gray-500 hover:text-accent font-body text-sm transition-colors">
                        (504) 818-3347
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-xl shrink-0">
                      <i className="ri-printer-line text-accent text-lg"></i>
                    </div>
                    <div>
                      <div className="font-heading font-bold text-navy text-sm mb-1">Fax</div>
                      <span className="text-gray-500 font-body text-sm">(504) 818-3349</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-navy rounded-2xl p-7">
                <h3 className="font-heading font-bold text-white text-base mb-4">Service Coverage</h3>
                <div className="flex flex-wrap gap-2">
                  {['Metairie', 'New Orleans', 'Kenner', 'Harahan', 'Jefferson', 'Elmwood', 'Gretna', 'Westwego'].map((c) => (
                    <span key={c} className="bg-accent/10 text-accent text-xs font-body px-3 py-1 rounded-full">{c}</span>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-7 border border-gray-100">
                <h3 className="font-heading font-bold text-navy text-base mb-4">Credentials</h3>
                <div className="space-y-2">
                  {['Licensed & Insured', 'GBAC COVID-19 Certified', 'GSA Federal Contractor', 'BBB Accredited', 'LA Contractor License #71638'].map((c) => (
                    <div key={c} className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-accent text-sm shrink-0"></i>
                      <span className="text-gray-600 font-body text-sm">{c}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <a href="https://www.bbb.org/us/la/metairie/profile/cleaning-services/ojs-janitorial-sweeping-service-llc-0985-90015985/" target="_blank" rel="nofollow noopener" className="cursor-pointer">
                    <img src="https://seal-neworleans.bbb.org/seals/blue-seal-150-110-bbb-90015985.png" alt="BBB Accredited" className="h-12 w-auto" />
                  </a>
                </div>
              </div>
            </div>

            {/* Right — Form + Map */}
            <div className="lg:col-span-2 space-y-8">
              {/* Form */}
              <div className="bg-white rounded-2xl p-10 border border-gray-100">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-6">
                      <i className="ri-checkbox-circle-line text-accent text-4xl"></i>
                    </div>
                    <h3 className="font-heading font-bold text-navy text-2xl mb-3">Quote Request Received!</h3>
                    <p className="text-gray-500 font-body text-base leading-relaxed max-w-sm mx-auto">
                      Thank you! A member of our team will contact you within 24 business hours to discuss your cleaning needs and provide a customized quote.
                    </p>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading font-extrabold text-navy text-2xl mb-2">Get a Free Cleaning Quote</h2>
                    <p className="text-gray-500 font-body text-sm mb-8">Fill out the form below and we'll respond within 24 hours with a customized quote.</p>
                    <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-navy font-body text-xs font-medium mb-2">Full Name *</label>
                          <input type="text" name="full_name" required placeholder="John Smith" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors" />
                        </div>
                        <div>
                          <label className="block text-navy font-body text-xs font-medium mb-2">Company Name</label>
                          <input type="text" name="company_name" placeholder="Your Business Name" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-navy font-body text-xs font-medium mb-2">Email Address *</label>
                          <input type="email" name="email" required placeholder="john@business.com" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors" />
                        </div>
                        <div>
                          <label className="block text-navy font-body text-xs font-medium mb-2">Phone Number *</label>
                          <input type="tel" name="phone" required placeholder="(504) 000-0000" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-navy font-body text-xs font-medium mb-2">Service Needed</label>
                          <select name="service_needed" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-navy focus:outline-none focus:border-accent transition-colors">
                            <option value="">Select a service...</option>
                            {SERVICES_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-navy font-body text-xs font-medium mb-2">City / Location</label>
                          <input type="text" name="city_location" placeholder="Metairie, LA" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-navy font-body text-xs font-medium mb-2">
                          Message / Additional Details
                          <span className="text-gray-400 ml-1">({charCount}/500)</span>
                        </label>
                        <textarea
                          name="message"
                          rows={5}
                          maxLength={500}
                          placeholder="Tell us about your facility size, number of employees, frequency of cleaning needed, and any special requirements..."
                          onChange={(e) => setCharCount(e.target.value.length)}
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors resize-none"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-accent hover:bg-accent-dark text-navy font-heading font-bold py-4 rounded-full transition-all duration-200 text-base whitespace-nowrap disabled:opacity-60 cursor-pointer"
                      >
                        {submitting ? 'Sending Your Request...' : 'Submit Quote Request →'}
                      </button>
                      <p className="text-gray-400 text-xs font-body text-center">
                        We respond within 24 business hours. Your information is kept private.
                      </p>
                    </form>
                  </>
                )}
              </div>

              {/* Map */}
              <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm h-72">
                <iframe
                  title="OJS Janitorial Office Location"
                  src="https://maps.google.com/maps?q=7809%20Airline%20Drive%20Suite%20307F%20Metairie%20LA%2070003&output=embed&z=15"
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
